
import React from 'react';
import { Helmet } from 'react-helmet';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Globe, Moon, Sun, Download, Upload, Shield } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';

const SettingsPage = () => {
  const { language, toggleLanguage } = useLanguage();

  const handleBackup = () => {
    const data = JSON.stringify(localStorage);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `stone_beam_backup_${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    toast({ title: "Backup Downloaded" });
  };

  const handleRestore = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
       try {
         const data = JSON.parse(e.target.result);
         // Restore items
         Object.keys(data).forEach(key => {
           localStorage.setItem(key, data[key]);
         });
         toast({ title: "System Restored", description: "Reloading application..." });
         setTimeout(() => window.location.reload(), 1500);
       } catch (err) {
         toast({ title: "Invalid Backup File", variant: "destructive" });
       }
    };
    reader.readAsText(file);
  };

  return (
    <>
      <Helmet><title>Settings - Stone Beam</title></Helmet>
      <div className="space-y-6 max-w-4xl">
        <h1 className="text-3xl font-bold text-slate-900">System Settings</h1>

        <Card>
          <CardHeader><CardTitle>Appearance & Language</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex items-center gap-3">
                <Globe className="w-5 h-5 text-slate-500" />
                <div>
                  <h3 className="font-medium">System Language</h3>
                  <p className="text-sm text-slate-500">{language === 'en' ? 'English' : 'Arabic'}</p>
                </div>
              </div>
              <Button variant="outline" onClick={toggleLanguage}>
                Switch to {language === 'en' ? 'Arabic' : 'English'}
              </Button>
            </div>

             <div className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex items-center gap-3">
                <Sun className="w-5 h-5 text-slate-500" />
                <div>
                  <h3 className="font-medium">Theme</h3>
                  <p className="text-sm text-slate-500">Currently using Light Mode</p>
                </div>
              </div>
              <Button variant="outline" disabled>
                Toggle Dark Mode (Pro)
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
           <CardHeader>
             <CardTitle>Data Management</CardTitle>
             <CardDescription>Backup and restore your local data</CardDescription>
           </CardHeader>
           <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 border rounded-lg bg-slate-50">
                 <div className="flex items-center gap-3">
                    <Download className="w-5 h-5 text-slate-500" />
                    <div>
                      <h3 className="font-medium">Export Backup</h3>
                      <p className="text-sm text-slate-500">Download all system data as JSON</p>
                    </div>
                 </div>
                 <Button onClick={handleBackup}>Download Backup</Button>
              </div>

              <div className="flex items-center justify-between p-4 border rounded-lg bg-slate-50">
                 <div className="flex items-center gap-3">
                    <Upload className="w-5 h-5 text-slate-500" />
                    <div>
                      <h3 className="font-medium">Import Data</h3>
                      <p className="text-sm text-slate-500">Restore from a backup file (Overwrites current data)</p>
                    </div>
                 </div>
                 <div className="relative">
                   <input type="file" onChange={handleRestore} className="absolute inset-0 opacity-0 cursor-pointer" />
                   <Button variant="destructive">Restore from File</Button>
                 </div>
              </div>
           </CardContent>
        </Card>
      </div>
    </>
  );
};

export default SettingsPage;
